// Food name and food category translations: HU → EN.
// Source data (foods.csv) stores Hungarian names; helpers translate to English
// when the active locale is 'en'. Missing entries fall back to the original.

import type { Lang } from './translations'

export const foodCategoryTranslations: Record<string, string> = {
  alapanyag: 'staple',
  fűszer: 'spice',
  hús: 'meat',
  ital: 'drink',
  pékáru: 'grain',
  tejtermék: 'dairy',
  zöldség: 'produce',
}

export function translateFoodCategory(category: string, lang: Lang): string {
  if (lang === 'hu') return category
  return foodCategoryTranslations[category] ?? category
}

export const foodNameTranslations: Record<string, string> = {
  // ── alapanyag (staples) ──
  'chlorella': 'chlorella',
  'ecet - balzsam': 'vinegar - balsamic',
  'ecet - cider': 'vinegar - cider',
  'édesítő - barna nádcukor': 'sweetener - brown cane sugar',
  'édesítő - juharszirup': 'sweetener - maple syrup',
  'édesítő - melasz': 'sweetener - molasses',
  'édesítő - méz': 'sweetener - honey',
  'kollagén - peptidek': 'collagen - peptides',
  'lecitin - napraforgó': 'lecithin - sunflower',
  'olaj - avokádó': 'oil - avocado',
  'olaj - dió': 'oil - walnut',
  'olaj - lenmag, hidegen préselt': 'oil - flaxseed, cold pressed',
  'olaj - mandula': 'oil - almond',
  'olaj - oliva': 'oil - olive',
  'olaj - szőlőmag': 'oil - grapeseed',
  'víz': 'water',
  'zsír - liba': 'fat - goose',
  'zsír - sertés': 'fat - pork',

  // ── fűszer (spices) ──
  'babérlevél': 'bay leaf',
  'bazsalikom - őrölt': 'basil - ground',
  'bors - fekete - őrölt': 'pepper - black - ground',
  'csili - őrölt': 'chili - ground',
  'curry': 'curry',
  'fahéj - őrölt': 'cinnamon - ground',
  'gyömbér - friss': 'ginger - fresh',
  'gyömbér - őrölt': 'ginger - ground',
  'kakukkfű - őrölt': 'thyme - ground',
  'kapor - friss': 'dill - fresh',
  'kapor - őrölt': 'dill - ground',
  'kömény - őrölt': 'cumin - ground',
  'koriander - mag': 'coriander - seed',
  'kurkuma - őrölt': 'turmeric - ground',
  'majoranna - őrölt': 'marjoram - ground',
  'oregánó - őrölt': 'oregano - ground',
  'petrezselyem - friss': 'parsley - fresh',
  'petrezselyem - őrölt': 'parsley - ground',
  'piros paprika - őrölt': 'paprika - ground',
  'só - nem jódozott': 'salt - non-iodized',
  'szegfűszeg - őrölt': 'clove - ground',
  'szerecsendió - őrölt': 'nutmeg - ground',
  'tárkony - őrölt': 'tarragon - ground',

  // ── hús (meat) ──
  'hal - hering - atlanti': 'fish - herring - atlantic',
  'hal - hering - csendes óceáni': 'fish - herring - pacific',
  'hal - kardhal': 'fish - swordfish',
  'hal - lazac - atlanti - tenyésztett': 'fish - salmon - atlantic - farmed',
  'hal - lazac - atlanti - vad': 'fish - salmon - atlantic - wild',
  'hal - lazac - Chinook': 'fish - salmon - Chinook',
  'hal - lazac - Chum': 'fish - salmon - Chum',
  'hal - lazac - Pink': 'fish - salmon - Pink',
  'hal - lazac - Sockeye': 'fish - salmon - Sockeye',
  'hal - makréla - atlanti': 'fish - mackerel - atlantic',
  'hal - makréla - csendes óceáni': 'fish - mackerel - pacific',
  'hal - makréla - King': 'fish - mackerel - King',
  'hal - makréla - Spanish': 'fish - mackerel - Spanish',
  'hal - naphal': 'fish - sunfish',
  'hal - nyelvhal - Solea solea': 'fish - sole - Solea solea',
  'hal - ördöghal': 'fish - monkfish',
  'hal - pisztráng': 'fish - trout',
  'hal - pisztráng - Szivárványos - tenyésztett': 'fish - trout - rainbow - farmed',
  'hal - pisztráng - Szivárványos - vad': 'fish - trout - rainbow - wild',
  'hal - pisztráng - tengeri': 'fish - trout - sea',
  'hal - tilápia': 'fish - tilapia',
  'hal - tokhal': 'fish - sturgeon',
  'hal - tonhal - Csíkoshasú': 'fish - tuna - Skipjack',
  'hal - tonhal - Kékúszójú': 'fish - tuna - Bluefin',
  'hal - tonhal - Sárgaúszójú': 'fish - tuna - Yellowfin',
  'marha - alaplé': 'beef - broth',
  'marha - bélszín (tenderloin)': 'beef - tenderloin',
  'marha - darált (20% zsír)': 'beef - ground (20% fat)',
  'marha - hátszín (sirloin)': 'beef - sirloin',
  'marha - lábszár': 'beef - shank',
  'marha - máj': 'beef - liver',
  'marha - nyelv': 'beef - tongue',
  'marha - pacal (tripe)': 'beef - tripe',
  'marha - rostélyos (chuck)': 'beef - chuck',
  'marha - szegy (brisket)': 'beef - brisket',
  'marha - szív': 'beef - heart',
  'marha - vese': 'beef - kidney',
  'sertés - comb (zsíros részek levágva)': 'pork - leg (fat trimmed)',
  'sertés - comb (zsíros részekkel)': 'pork - leg (with fat)',
  'sertés - darált (4% zsír)': 'pork - ground (4% fat)',
  'sertés - darált (16% zsír)': 'pork - ground (16% fat)',
  'sertés - has (dagadó)': 'pork - belly',
  'sertés - hát szalonna': 'pork - back bacon',
  'sertés - karaj (zsíros részek levágva)': 'pork - loin (fat trimmed)',
  'sertés - karaj (zsíros részekkel)': 'pork - loin (with fat)',
  'sertés - lapocka (zsíros részek levágva)': 'pork - shoulder (fat trimmed)',
  'sertés - lapocka (zsíros részekkel)': 'pork - shoulder (with fat)',
  'sertés - máj': 'pork - liver',
  'sertés - szív': 'pork - heart',
  'sertés - szűzpecsenye (zsíros részek levágva)': 'pork - tenderloin (fat trimmed)',
  'sertés - szűzpecsenye (zsíros részekkel)': 'pork - tenderloin (with fat)',
  'szárnyas - csirke - alaplé': 'poultry - chicken - broth',
  'szárnyas - csirke - comb (bőrös)': 'poultry - chicken - thigh (with skin)',
  'szárnyas - csirke - máj': 'poultry - chicken - liver',
  'szárnyas - csirke - mell (bőrnélkül)': 'poultry - chicken - breast (skinless)',
  'szárnyas - csirke - szív': 'poultry - chicken - heart',
  'szárnyas - csirke - zuza': 'poultry - chicken - gizzard',
  'szárnyas - pulyka - máj': 'poultry - turkey - liver',
  'szárnyas - pulyka - mell (bőrös)': 'poultry - turkey - breast (with skin)',
  'szárnyas - pulyka - szív': 'poultry - turkey - heart',
  'szárnyas - pulyka - zuza': 'poultry - turkey - gizzard',
  'szárnyas - tyúk - tojás (egész)': 'poultry - chicken - egg (whole)',

  // ── ital (drinks) ──
  'ital - kávé - főzött espresso': 'drink - coffee - brewed espresso',
  'ital - kávé - főzött espresso - decaf': 'drink - coffee - brewed espresso - decaf',
  'ital - kókuszvíz': 'drink - coconut water',
  'ital - tea - kamilla (főzött)': 'drink - tea - chamomile (brewed)',
  'ital - tea - zöld (főzött)': 'drink - tea - green (brewed)',

  // ── pékáru (grains, nuts, seeds) ──
  'fenyőmag': 'pine nut',
  'gabona - búza - liszt - teljes kiőrlésű': 'grain - wheat - flour - whole',
  'gabona - búzakorpa': 'grain - wheat bran',
  'gabona - rizskorpa': 'grain - rice bran',
  'gabona - rozs - liszt': 'grain - rye - flour',
  'gabona - tönköly': 'grain - spelt',
  'gabona - tritikálé - liszt': 'grain - triticale - flour',
  'gabona - zab - pehely': 'grain - oat - flakes',
  'gabona - zabkorpa': 'grain - oat bran',
  'goji bogyó - szárított': 'goji berry - dried',
  'kender - mag': 'hemp - seed',
  'lenmag': 'flaxseed',
  'makadámdió': 'macadamia nut',
  'mandula': 'almond',
  'mazsola - magnélküli': 'raisin - seedless',
  'mogyoró - földi - héjban - Virginia': 'peanut - in shell - Virginia',
  'mogyoró - földi - magozott - Spanish': 'peanut - shelled - Spanish',
  'mogyoró török': 'hazelnut',
  'napraforgómag - pörkölt': 'sunflower seed - roasted',
  'pisztácia - pörkölt': 'pistachio - roasted',
  'szezámmag - egész': 'sesame seed - whole',
  'szezámmag - hántolt': 'sesame seed - hulled',
  'tökmag': 'pumpkin seed',

  // ── tejtermék (dairy) ──
  'camembert': 'camembert',
  'cheddar': 'cheddar',
  'csokoládé - ét 70%': 'chocolate - dark 70%',
  'csokoládé - fehér': 'chocolate - white',
  'edami': 'edam',
  'feta': 'feta',
  'ghí': 'ghee',
  'gouda': 'gouda',
  'joghurt - görög - low fat': 'yogurt - greek - low fat',
  'joghurt - natúr - low fat': 'yogurt - plain - low fat',
  'joghurt - natúr - whole milk': 'yogurt - plain - whole milk',
  'kefír - alacsony zsírtartalmú': 'kefir - low fat',
  'mozzarella - light': 'mozzarella - light',
  'mozzarella - teljes tejből': 'mozzarella - whole milk',
  'parmezán': 'parmesan',
  'svájci sajt (pl.: ementáli)': 'swiss cheese (e.g. emmental)',
  'tej - kókusz': 'milk - coconut',
  'tej - tehén - 2%': 'milk - cow - 2%',
  'tej - tehén - 3,7%': 'milk - cow - 3.7%',
  'tejföl - 20%': 'sour cream - 20%',
  'tejszín - 12%': 'cream - 12%',
  'tofu (szójababsajt)': 'tofu (soybean curd)',
  'túró - krémállagú': 'curd cheese - creamy',
  'túró - zsírszegény': 'curd cheese - low fat',
  'vaj - 80% zsír': 'butter - 80% fat',

  // ── zöldség (produce: vegetables + fruits) ──
  'áfonya': 'blueberry',
  'alma - gala': 'apple - gala',
  'alma - golden delicious': 'apple - golden delicious',
  'alma - gránát': 'apple - granate',
  'alma - granny smith': 'apple - granny smith',
  'ananász': 'pineapple',
  'avokádó': 'avocado',
  'bab - hüvelyes - sárga - friss': 'bean - pod - yellow - fresh',
  'bab - hüvelyes - zöld - friss': 'bean - pod - green - fresh',
  'bab - kis fehér': 'bean - small white',
  'bab - tarka': 'bean - speckled',
  'bab - vese': 'bean - kidney',
  'banán - főző - sárga': 'plantain - yellow',
  'banán - főző - zöld': 'plantain - green',
  'banán - normál': 'banana',
  'barack - sárga': 'peach - yellow',
  'borsó - csicseri': 'pea - chickpea',
  'borsó - sárga': 'pea - yellow',
  'borsó - zöld': 'pea - green',
  'brazildió': 'brazil nut',
  'brokkoli': 'broccoli',
  'bulgur': 'bulgur',
  'burgonya - édes': 'sweet potato',
  'búzacsíra': 'wheat germ',
  'cékla': 'beetroot',
  'cékla - savanyúság': 'beetroot - pickled',
  'chia mag': 'chia seed',
  'citrom': 'lemon',
  'cseresznye': 'cherry',
  'cukkini - héjával': 'zucchini - with skin',
  'datolya - deglet noor': 'date - deglet noor',
  'datolya - medjool': 'date - medjool',
  'dinnye - görög': 'watermelon',
  'dinnye - sárga': 'cantaloupe',
  'dió - kesu': 'nut - cashew',
  'dió - normál': 'walnut',
  'dió - pekán': 'pecan',
  'eper - normál': 'strawberry',
  'feketeribizli': 'blackcurrant',
  'füge - száritott': 'fig - dried',
  'gesztenye': 'chestnut',
  'ginkgo mag - szárított': 'ginkgo seed - dried',
  'gomba - csiperke': 'mushroom - white button',
  'grapefruit - fehér': 'grapefruit - white',
  'grapefruit - piros': 'grapefruit - red',
  'hagyma - fok': 'garlic',
  'hagyma - normál': 'onion',
  'hajdina': 'buckwheat',
  'Hántolt árpa (gersli)': 'pearl barley',
  'káposzta - fejes': 'cabbage - head',
  'káposzta - kel': 'cabbage - savoy',
  'káposzta - vörös': 'cabbage - red',
  'karalábé': 'kohlrabi',
  'karfiol': 'cauliflower',
  'kel - bimbó': 'brussels sprout',
  'kiwi': 'kiwi',
  'köles': 'millet',
  'körte - bartlett (vilmos)': 'pear - bartlett',
  'körte - bosc': 'pear - bosc',
  'körte - green anjou': 'pear - green anjou',
  'kukorica - csemege konzerv': 'corn - sweet canned',
  'lencse - normál - száraz': 'lentil - dried',
  'lime': 'lime',
  'mák - darált': 'poppy seed - ground',
  'málna': 'raspberry',
  'mandarin': 'mandarin',
  'mangó': 'mango',
  'meggy': 'sour cherry',
  'narancs': 'orange',
  'nektarin': 'nectarine',
  'olivabogyó - magozott': 'olive - pitted',
  'Orvosi vízitorma': 'watercress',
  'padlizsán': 'eggplant',
  'papaja': 'papaya',
  'paprika - chilli - red': 'pepper - chilli - red',
  'paprika - jalapeno': 'pepper - jalapeno',
  'paprika - kaliforniai - piros': 'pepper - bell - red',
  'paprika - tv': 'pepper - hungarian wax',
  'paradicsom - normál': 'tomato',
  'paradicsom - pürésített': 'tomato - puree',
  'paradicsom - sűrítmény': 'tomato - paste',
  'quinoa': 'quinoa',
  'répa - fehér': 'carrot - white',
  'répa - sárga': 'carrot - yellow',
  'retek - normál': 'radish',
  'retek - vaj': 'radish - butter',
  'rizs - barna': 'rice - brown',
  'rukkola': 'arugula',
  'saláta - fejes': 'lettuce - head',
  'saláta - jég': 'lettuce - iceberg',
  'spárga': 'asparagus',
  'spenót': 'spinach',
  'szeder': 'blackberry',
  'szilva': 'plum',
  'szőlő - fehér vagy piros - thompson seedless': 'grape - thompson seedless',
  'tök - csillag': 'squash - pattypan',
  'tök - sütő': 'squash - baking',
  'torma - reszelt': 'horseradish - grated',
  'uborka - kígyó - hámozott': 'cucumber - long - peeled',
  'uborka - kovászos - dill': 'cucumber - pickled - dill',
  'uborka - kovászos - sour': 'cucumber - pickled - sour',
  'zeller - gumó': 'celery root',
}

export function translateFoodName(name: string, lang: Lang): string {
  if (lang === 'hu') return name
  return foodNameTranslations[name] ?? name
}
